import { useCallback, useEffect, useRef, useState } from 'react'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { Divider, FlexboxGrid } from 'rsuite'
import dynamic from 'next/dynamic'

import { getSupplierCode, isSupplier } from '../../lib/supplier'
import {
  MediaType,
  Product,
  Supplier,
  SupplierMappedProductsProps,
} from '../../utils/types/Product'
import { CountryData, ScreenSize } from '../../utils/constants'
import { getSupplierDeliveryDetails } from '../../lib/service'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import ProductUnavailable from './ProductUnavailable'
import CategorySpecificCarousel from './CategorySpecificCarousel'
import ProductNavigation from './AttributesSection/ProductNavigation'
import ProductImage from './ProductImage'
import ProductAttributes from './AttributesSection/ProductAttributes'
import BreadcrumbCategory from './BreadcrumbCategory'
import HighlightTag from '../HighlightTag'
import SupplierCarousel from '../Catalog/SupplierCatalog/SupplierCarousel'
import CatalogBottomSection from '../Catalog/CatalogBottomSection/CatalogBottomSection'
import ProductSupplierHeading from './ProductSupplierHeading'
import EditProductIcon from '../Icons/EditProductIcon'
import ProductColumn from './ProductColumn'
import useEcomEvents from '../../services/analytics/useEcomEvents'
import useIsOnMobile from '../../services/useIsOnMobile'
import useProductQuantity from './hooks/useProductQuantity'
import useTranslateToDefaultLanguage from './hooks/useTranslateToDefaultLanguage'
import { useAuth } from '../../services/useAuth'
import useInternalTraffic from '../hooks/useInternalTraffic'
import withRequireAuth from '../../services/withRequireAuth'
import { getOrderedProductCategories } from '../../lib/product'
import ProductEmptyImage from './ProductEmptyImage'
import { useCart } from '../Cart/CartProvider'
import { getSupplierMovData } from '../../lib/cart'
import SourceToOrder from '../SourceToOrder'
import ProductCarousel from '../Carousels/ProductCarousel'
import useOthersYouMayLike from '../../services/recommendationsAPI/useOthersYouMayLike'
import useSimilarItems from '../../services/recommendationsAPI/useSimilarItems'
import { AnalyticsProductCarouselTypes } from '../../utils/types/analytics'
import useCyModel from '../../services/useCyModel'

import styles from '../../styles/Product.module.less'

const OrderModal = dynamic(() => import('../Modals/OrderModal'))

export interface SingleProductViewProps {
  products: Product[]
  product: Product
  supplier?: Supplier
  addToCartLink: string
  media: MediaType[]
}

const SingleProductView: React.FC<SingleProductViewProps> = (props) => {
  const {
    products,
    supplier,
    addToCartLink,
    product,
    media,
  } = props

  const [orderModal, toggleOrderModal] = useState(false)

  const { t } = useTranslation('products')
  const { locale: countryAndLocale } = useRouter()
  const { viewItemEvent } = useEcomEvents()
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const isOnTablet = useIsOnMobile(ScreenSize.lg)

  const orderedCategories = getOrderedProductCategories(product)
  const [productQuantity, setProductQuantity] = useProductQuantity(
    product,
    supplier,
  )

  const { user, isVatIncluded } = useAuth()
  const { isInternalTraffic } = useInternalTraffic()

  const productReviewsRef = useRef<HTMLDivElement>(null)
  const { modifiedCart } = useCart()

  const {
    data: othersYouMayLike,
    isLoading: isOthersYouMayLikeLoading,
    triggerDetailPageView: triggerOthersYouMayLikeDetailPageView,
    isInitialCartLoaded,
  } = useOthersYouMayLike({ pageSize: 15 })

  const {
    data: similarProducts,
    triggerDetailPageView: triggerSimilarProductsDetailPageView,
    isLoading: isSimilarProductsLoading,
  } = useSimilarItems({
    associatedItems: [product['product.code']],
    pageSize: 15,
  })

  const { country } = getCountryAndLocaleStrings(countryAndLocale)

  const countryName = CountryData.find((item) => item.locale === country)?.name
  const supplierMappedProducts: SupplierMappedProductsProps | undefined = supplier ? {
    ...supplier,
    product: products.filter((prod) => prod.supplier?.[0]?.['supplier.id'] === supplier?.['supplier.id']),
  } : undefined

  const {
    isAvailable: isDeliveryAvailable,
  } = getSupplierDeliveryDetails(supplier, country)

  const {
    supplierTotal,
  } = getSupplierMovData(supplier?.['supplier.code'] || '', country, modifiedCart, isVatIncluded)

  // Refs for single product navigation
  const productFeaturesRef = useRef<HTMLDivElement>(null)
  const productStandardsRef = useRef<HTMLDivElement>(null)
  const deliveryTermsRef = useRef<HTMLDivElement>(null)
  const productDocumentsRef = useRef<HTMLDivElement>(null)
  const supplierProductsRef = useRef<HTMLDivElement>(null)
  const productNavigationRefs = {
    productFeaturesRef,
    productStandardsRef,
    deliveryTermsRef,
    productDocumentsRef,
    supplierProductsRef,
  }

  // Translate to default language:
  // needed since we only deploy with default locale currently
  useTranslateToDefaultLanguage(product)

  const handleScrollToReviews = useCallback(() => {
    if (productReviewsRef.current) {
      productReviewsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    // Load product recommendations
    triggerSimilarProductsDetailPageView()
  }, [supplierTotal, product['product.code']])

  // Fire analytics event
  useEffect(() => {
    if (isDeliveryAvailable) {
      viewItemEvent(product) // Analytics event
    }
  }, [product?.['product.code']])

  // Pass cart and previous viewed products to others you may like trigger
  // Only trigger once, and only after cart has loaded
  useEffect(() => {
    if (isInitialCartLoaded) {
      triggerOthersYouMayLikeDetailPageView([product['product.code']])
    }
  }, [isInitialCartLoaded])

  useCyModel('SingleProduct', {
    product,
    openOrderRequestModal: (isVisible: boolean) => toggleOrderModal(isVisible),
  })

  // Return error page in case product isn't loaded
  if (!product) {
    // The static generated page does not contain product data
    return (
      <ErrorPage
        statusCode={404}
        title={t('common:This product data could not be found')}
      />
    )
  }

  const productSKU = product?.['product.code'].toUpperCase()
  const doesUserOwnTheProduct = user && getSupplierCode(user?.name) === product?.supplier[0]?.['supplier.code']

  // Display the edit icon only if
  // the user is a superuser
  // or the user is the supplier of the product
  const shouldDisplayEditIcon = isInternalTraffic
      && user
      && (!!user.superuser || (isSupplier(user) && doesUserOwnTheProduct))

  const editProductPageLink = `/manage/product/${productSKU}?site=${user?.siteid}`
  const supplierName = supplier?.['supplier.label']

  const EditProductLink = withRequireAuth(() => (
    <Link
      passHref
      href={editProductPageLink}
      legacyBehavior
    >
      <a target="_blank">
        <EditProductIcon />
      </a>
    </Link>
  ))

  return (
    <>
      {shouldDisplayEditIcon && <EditProductLink />}
      <div
        data-testid="single-product-page"
        className={`${styles['single-product-page']} max-width-m`}
      >

        <BreadcrumbCategory
          orderedCategories={orderedCategories}
          product={product}
          supplierName={supplierName}
        />
        {isDeliveryAvailable ? (
          <>
            <FlexboxGrid className={styles['product-main-section']}>
              {isOnMobile
                ? (
                  <FlexboxGrid.Item
                    colspan={15}
                    className={`${styles['left-column']} ${styles['image-gallery-wrapper']}`}
                  >
                    <ProductSupplierHeading
                      product={product}
                      supplier={supplier}
                      handleScrollToReviews={handleScrollToReviews}
                    />

                    <HighlightTag
                      label={product['product.highlight']}
                      productProperty={product['product/property']}
                      attributes={product.attribute}
                      className={styles['product-highlight-tag']}
                      type={['label', 'feature-type', 'standards-test']}
                    />

                    {media?.length ? (
                      <ProductImage media={media} />)
                      : (
                        <ProductEmptyImage
                          height={500}
                          width={500}
                        />
                      )}

                    <ProductColumn
                      product={product}
                      supplier={supplier}
                      productQuantity={productQuantity}
                      addToCartLink={addToCartLink}
                      setProductQuantity={setProductQuantity}
                      toggleOrderModal={toggleOrderModal}
                    />

                    <div className="margin-top-spacer-tripple">
                      <ProductNavigation navigationRefs={productNavigationRefs} />
                    </div>

                    <Divider className={styles['product-navigation-divider']} />
                    <ProductAttributes
                      product={product}
                      supplier={supplier}
                      productNavigationRefs={productNavigationRefs}
                    />
                  </FlexboxGrid.Item>
                )
                : (
                  <>
                    <FlexboxGrid.Item
                      colspan={isOnTablet ? 12 : 14}
                      className={`${styles['left-column']} ${styles['image-gallery-wrapper']}`}
                    >
                      <ProductSupplierHeading
                        product={product}
                        supplier={supplier}
                        handleScrollToReviews={handleScrollToReviews}
                      />

                      {media?.length ? (
                        <ProductImage media={media} />)
                        : (
                          <ProductEmptyImage
                            height={500}
                            width={500}
                          />
                        )}

                      <HighlightTag
                        label={product['product.highlight']}
                        productProperty={product['product/property']}
                        attributes={product.attribute}
                        className={styles['highlight-tag']}
                        type={['label', 'feature-type', 'standards-test']}
                      />

                      <ProductNavigation navigationRefs={productNavigationRefs} />

                      <Divider className={styles['product-navigation-divider']} />

                      <ProductAttributes
                        product={product}
                        supplier={supplier}
                        productNavigationRefs={productNavigationRefs}
                      />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item
                      colspan={isOnTablet ? 12 : 10}
                      className={styles['right-column']}
                    >
                      <ProductColumn
                        product={product}
                        supplier={supplier}
                        productQuantity={productQuantity}
                        addToCartLink={addToCartLink}
                        setProductQuantity={setProductQuantity}
                        toggleOrderModal={toggleOrderModal}
                      />
                    </FlexboxGrid.Item>
                  </>
                )}
            </FlexboxGrid>

            <div className={`${styles['bottom-section']} margin-top-spacer-quadruple`}>
              <ProductCarousel
                carouselType={AnalyticsProductCarouselTypes.OthersYouMayLike}
                productCardType="narrow"
                title={t('products:Other products you may like')}
                products={othersYouMayLike}
                isLoading={isOthersYouMayLikeLoading}
                carouselGap={isOnMobile ? 20 : 25}
                className="margin-top-spacer-double"
              />

              <ProductCarousel
                carouselType={AnalyticsProductCarouselTypes.SimilarItems}
                productCardType="narrow"
                title={t('products:Similar products you may like')}
                products={similarProducts}
                isLoading={isSimilarProductsLoading}
                carouselGap={isOnMobile ? 20 : 25}
                className="margin-top-spacer-quadruple"
              />

              {supplierMappedProducts && (
                <SupplierCarousel
                  ref={supplierProductsRef}
                  supplier={supplierMappedProducts}
                  filterProductIds={[product.id]}
                  className="padding-bottom-spacer-quadruple"
                />
              )}

              <SourceToOrder />
            </div>

            <CatalogBottomSection
              product={product}
              ref={productReviewsRef}
            />
          </>
        )
          : (
            <>
              <ProductUnavailable
                mainTitle={t('The product is not available in {{ country }}', { country: t(`countries:${countryName}`) ?? '' })}
                descText={t('Change the country of delivery or ask for an alternative for product code {{ prodCode }} by contacting one of our product experts', { prodCode: product['product.code'] })}
                product={product}
                orderedCategories={orderedCategories}
              />
              <CategorySpecificCarousel
                categories={orderedCategories}
                className="margin-top-spacer-double padding-top-spacer-quadruple"
              />
            </>
          )}
        {orderModal && (
          <OrderModal
            open={orderModal}
            setOpen={toggleOrderModal}
            product={product}
            productQuantity={productQuantity}
          />
        )}
      </div>
    </>
  )
}

export default SingleProductView
